import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { FeedbackForm } from '../../components/course/FeedbackForm';
import { CourseApiError, useCourseState, useStartCourse } from '../../hooks/useCourse';
import { useMe } from '../../hooks/useMe';
import { requestBotWriteAccess } from '../../lib/telegram';
import { stripInline } from '@wds/astro-bot-shared/course/inline';
import { courseEnrolSource } from '@wds/astro-bot-shared/course/link';
import { isFirstHistoryEntry } from '../../lib/history-back';
import { itemLabel, itemPath } from '../../lib/course-items';
import type { CourseItemDto, CourseItemState } from '../../types/course';
import styles from './CoursePage.module.css';

const MARK: Record<CourseItemState, string> = { done: '✓', current: '→', locked: '🔒', waiting: '⏳' };

/** Enrolment source: as the bot does for a course link (valid tag, else 'start'); 'miniapp' when opened without one. */
function startSource(): string {
  const param = (WebApp?.initDataUnsafe as { start_param?: string } | undefined)?.start_param ?? '';
  return courseEnrolSource(param) ?? 'miniapp';
}

function openChat(url: string): void {
  try {
    WebApp.openTelegramLink(url);
  } catch {
    window.open(url, '_blank');
  }
}

export function CoursePage(): JSX.Element {
  const { t } = useTranslation('course');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useCourseState();
  const start = useStartCourse();
  const { data: me } = useMe();
  const [busy, setBusy] = useState(false);
  const showRuNote = !!me && me.language !== 'ru';

  async function handleStart(): Promise<void> {
    if (busy || start.isPending) return;
    setBusy(true);
    // Ask for permission to write first: reminders and "next lesson is open" go through the bot chat.
    // It never rejects; a refusal does not stop the course, the first reminder just gets 403 and goes quiet.
    try {
      await requestBotWriteAccess();
      await start.mutateAsync({ source: startSource() });
    } catch {
      /* start.isError drives the message */
    } finally {
      setBusy(false);
    }
  }

  function renderItem(it: CourseItemDto, isOwner: boolean): JSX.Element {
    const open = it.state === 'done' || it.state === 'current';
    return (
      <li key={it.code}>
        <button
          type="button"
          className={`${styles.item} ${styles[`item_${it.state}`] ?? ''}`}
          disabled={!open}
          aria-label={`${stripInline(it.title)} — ${t(`state.${it.state}`)}`}
          onClick={() => open && navigate(itemPath(it.kind, it.code))}
          data-testid={`course-item-${it.code}`}
        >
          <span className={styles.mark} aria-hidden="true">{MARK[it.state]}</span>
          <span className={styles.itemTitle}>
            {itemLabel(t, it)}
          </span>
          {isOwner && it.draft && <span className={styles.draft}>{t('draft')}</span>}
        </button>
      </li>
    );
  }

  const aboutLink = (
    <button type="button" className={styles.link} onClick={() => navigate('/course/about')} data-testid="course-about">
      {t('about.link')} ›
    </button>
  );

  let body: JSX.Element;
  if (isLoading) {
    body = <p data-testid="course-loading">{tc('states.loading')}</p>;
  } else if (!data) {
    body = <p role="alert" data-testid="course-error">{tc('errors.generic')}</p>;
  } else if (!data.launched) {
    body = (
      <>
        <h2 className={styles.heading}>{t('title')}</h2>
        <p className={styles.status} data-testid="course-not-launched">{t('notLaunched')}</p>
        {aboutLink}
      </>
    );
  } else if (!data.enrolled) {
    body = (
      <>
        <h2 className={styles.heading}>{t('title')}</h2>
        {showRuNote && <p className={styles.note} data-testid="course-ru-note">{t('ruOnly')}</p>}
        <ul className={styles.intro}>
          <li>{t('intro.line1')}</li>
          <li>{t('intro.line2')}</li>
          <li>{t('intro.line3')}</li>
        </ul>
        <button
          type="button"
          className={styles.primary}
          onClick={() => void handleStart()}
          disabled={busy || start.isPending}
          data-testid="course-start"
        >
          {busy || start.isPending ? t('starting') : t('start')}
        </button>
        {start.isError && <p role="alert" data-testid="course-start-error">{t('startError')}</p>}
        {aboutLink}
      </>
    );
  } else {
    const currentItem = data.current
      ? data.blocks.flatMap((b) => b.items).find((i) => i.code === data.current!.code)
      : undefined;
    body = (
      <>
        {isError && <p role="alert" className={styles.note} data-testid="course-refresh-failed">{t(error instanceof CourseApiError && error.code === 'not_launched' ? 'refreshNotLaunched' : 'refreshFailed')}</p>}
        <h2 className={styles.heading}>{t('title')}</h2>
        {showRuNote && <p className={styles.note} data-testid="course-ru-note">{t('ruOnly')}</p>}
        <p className={styles.progress} data-testid="course-progress">
          {t('progress', { done: data.progress.lessonsDone, total: data.progress.lessonsTotal })}
        </p>
        {data.status === 'completed' && <p className={styles.status} data-testid="course-completed">{t('completed')}</p>}
        {data.status === 'waiting_approval' && (
          <p className={styles.status} data-testid="course-waiting">
            {t(data.progress.lessonsDone === 0 ? 'waitingFirst' : 'waiting')}
          </p>
        )}
        {data.current && data.status === 'in_progress' && (
          <button
            type="button"
            className={styles.primary}
            onClick={() => navigate(itemPath(data.current!.kind, data.current!.code))}
            data-testid="course-continue"
          >
            {t('continueLesson', { title: itemLabel(t, { kind: data.current.kind, number: currentItem?.number ?? null, title: data.current.title }) })}
          </button>
        )}
        {data.blocks.map((b) => (
          <section key={b.position} className={styles.block}>
            <h3 className={styles.blockTitle}>{b.title}</h3>
            <ul className={styles.items}>{b.items.map((it) => renderItem(it, data.isOwner))}</ul>
          </section>
        ))}
        {/* The learner has finished everything published (plan O4): feedback about the whole course. */}
        {(data.status === 'completed' || data.status === 'waiting_approval') && (
          <section className={styles.feedback} data-testid="course-feedback">
            <FeedbackForm />
          </section>
        )}
        <div className={styles.links}>
          <button type="button" className={styles.link} onClick={() => openChat(data.chatUrl)} data-testid="course-chat">
            {t('communityChat')} ›
          </button>
          {aboutLink}
        </div>
      </>
    );
  }

  return (
    <MeSubpageShell
      title={t('pageTitle')}
      // Opened by a startapp link: there is nothing to go back to, so leave to `/` (normal start / onboarding).
      onBack={() => (isFirstHistoryEntry() ? navigate('/', { replace: true }) : navigate(-1))}
      // After onboarding this page is a tab; before it (a course link) the back button leads to the normal start.
      hideBack={(me?.onboardingStep ?? 0) >= 99}
      data-testid="course-page"
    >
      <div className={styles.body}>{body}</div>
    </MeSubpageShell>
  );
}
